import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'

import type { SearchableLocation } from '@/features/jobs/types/searchable-location'
import type { SelectOption } from '@/shared/components'
import type { PaginatedResult } from '@/lib/api/types'

const LOCATION_SEARCH_DEBOUNCE_MS = 300

const formatLocationOptionLabel = (location: SearchableLocation) => {
  const details = [location.site_reference, location.road_name, location.town]
    .filter(Boolean)
    .join(', ')

  return details ? `${location.location_id} — ${details}` : location.location_id
}

const locationSearchHaystack = (location: SearchableLocation) =>
  [
    location.location_id,
    location.site_reference,
    location.road_name,
    location.town,
    location.county,
    location.asset_location,
    location.what3words,
    location.postcode,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

const locationMatchesQuery = (location: SearchableLocation, query: string) => {
  const normalized = query.trim().toLowerCase()

  return !normalized || locationSearchHaystack(location).includes(normalized)
}

type LocationSearchFn = (
  query: string | undefined,
  page: number
) => Promise<PaginatedResult<SearchableLocation[]>>

type UseLocationSearchOptions = {
  enabled?: boolean
  search: LocationSearchFn
}

export const useLocationSearch = ({
  enabled = true,
  search,
}: UseLocationSearchOptions) => {
  const [locations, setLocations] = useState<SearchableLocation[]>([])
  const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>()
  const [locationInputValue, setLocationInputValue] = useState('')
  const [selectedLocationLabel, setSelectedLocationLabel] = useState<string | null>(
    null
  )
  const [hasOpenedLocationPicker, setHasOpenedLocationPicker] = useState(false)
  const [wasEnabled, setWasEnabled] = useState(enabled)
  const [completedSearchQuery, setCompletedSearchQuery] = useState<string | null>(
    null
  )
  const [searchError, setSearchError] = useState<string | null>(null)
  const [debouncedLocationQuery] = useDebounce(
    locationInputValue,
    LOCATION_SEARCH_DEBOUNCE_MS
  )

  if (enabled !== wasEnabled) {
    setWasEnabled(enabled)

    if (!enabled) {
      setHasOpenedLocationPicker(false)
      setCompletedSearchQuery(null)
    }
  }

  const searchQuery = debouncedLocationQuery.trim()
  const isQueryForSelectedLocation =
    selectedLocationLabel !== null && searchQuery === selectedLocationLabel
  const shouldSearch =
    enabled && hasOpenedLocationPicker && !isQueryForSelectedLocation
  const isSearchingLocations =
    shouldSearch && completedSearchQuery !== searchQuery

  useEffect(() => {
    if (!shouldSearch) {
      return
    }

    let isCurrent = true

    search(searchQuery || undefined, 1).then(
      (result) => {
        if (isCurrent) {
          setLocations(result.data)
          setCompletedSearchQuery(searchQuery)
        }
      },
      (error: unknown) => {
        if (isCurrent) {
          setLocations([])
          setSearchError(
            error instanceof Error ? error.message : 'Failed to search locations'
          )
          setCompletedSearchQuery(searchQuery)
        }
      }
    )

    return () => {
      isCurrent = false
    }
  }, [search, searchQuery, shouldSearch])

  const locationSelectOptions = useMemo<SelectOption[]>(() => {
    const visibleLocations = locations.filter((location) =>
      locationMatchesQuery(location, locationInputValue)
    )

    return visibleLocations.map((location) => ({
      value: String(location.id),
      label: formatLocationOptionLabel(location),
    }))
  }, [locationInputValue, locations])

  const selectLocation = useCallback((value: string | undefined) => {
    setSelectedLocationId(value)

    if (!value) {
      setSelectedLocationLabel(null)
      return undefined
    }

    const location = locations.find((item) => String(item.id) === value)
    if (!location) {
      return undefined
    }

    setSelectedLocationLabel(location.location_id)
    setLocationInputValue(location.location_id)
    return location
  }, [locations])

  const changeLocationInput = useCallback((nextValue: string) => {
    setLocationInputValue(nextValue)
    setSearchError(null)

    if (
      selectedLocationLabel !== null &&
      nextValue.trim() !== selectedLocationLabel
    ) {
      setSelectedLocationId(undefined)
      setSelectedLocationLabel(null)
    }
  }, [selectedLocationLabel])

  const openLocationPicker = useCallback(() => {
    setHasOpenedLocationPicker(true)
  }, [])

  const hydrateFromQuery = useCallback((query: string) => {
    setLocationInputValue(query)
    setSelectedLocationId(undefined)
    setSelectedLocationLabel(null)
    setLocations([])
    setHasOpenedLocationPicker(false)
    setCompletedSearchQuery(null)
    setSearchError(null)
  }, [])

  return {
    locationSelectOptions,
    selectedLocationId,
    locationInputValue,
    isSearchingLocations,
    searchError,
    selectLocation,
    changeLocationInput,
    openLocationPicker,
    hydrateFromQuery,
  }
}
